import { ApolloClient, ApolloLink, split, createHttpLink } from '@apollo/client'
import { InMemoryCache } from '@apollo/client/cache'
import { setContext } from '@apollo/client/link/context'
import { WebSocketLink } from '@apollo/client/link/ws'
import { getMainDefinition } from '@apollo/client/utilities'
import { createNetworkStatusNotifier } from 'react-apollo-network-status'

import { GetTokenSilentlyOptions } from '@auth0/auth0-spa-js/dist/typings/global'
import generatedIntrospection from '../../generated/fragmentTypes.json'

const studentApiUri = process.env.REACT_APP_STUDENT_API_GQL_URI
const studentWsUri = process.env.REACT_APP_STUDENT_API_WS_URI

const { link: statusLink, useApolloNetworkStatus } = createNetworkStatusNotifier()

export const useStudentNetworkStatus = useApolloNetworkStatus

const getStudentClient = (
  getTokenSilently: (o?: GetTokenSilentlyOptions | undefined) => Promise<string | undefined>
) => {
  const authLink = setContext(async (_, { headers, isPublicQuery }) => {
    if (isPublicQuery) {
      return { ...headers }
    }
    const accessToken = await getTokenSilently()
    return {
      headers: {
        ...headers,
        authorization: accessToken ? `Bearer ${accessToken}` : ''
      }
    }
  })
  const http = createHttpLink({
    uri: studentApiUri
  })
  const wsLink = new WebSocketLink({
    uri: studentWsUri!,
    options: {
      reconnect: true
    }
  })
  const splitLink = split(
    // split based on operation type
    ({ query }) => {
      const definition = getMainDefinition(query)
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
    },
    wsLink,
    http
  )

  const link = ApolloLink.from([authLink, statusLink, splitLink])
  const cache = new InMemoryCache({
    possibleTypes: generatedIntrospection.possibleTypes,
    typePolicies: {
      PlanAlert: {
        keyFields: ['alertKey', 'itemId']
      },
      PlanGroup: {
        fields: {
          items: {
            merge(_existing, incoming: any[]) {
              return incoming
            }
          }
        }
      }
    }
  })

  const studentClient = new ApolloClient({
    link,
    cache,
    connectToDevTools: process.env.NODE_ENV !== 'production'
  })

  return studentClient
}

export default getStudentClient
